import { CategoryIcon } from '@/components/CategoryIcon';

/**
 * Resolved tile colors. Every surface derives this through the ONE color
 * system — `resolveCategoryColor` + `categoryChipStyleFromColor` (via
 * `categoryVisual` or `categoryChipStyleFromMeta`) — so the same ref renders
 * the same tile everywhere.
 */
export interface CategoryTileVisual {
  backgroundColor: string;
  color: string;
}

const RING_RADIUS = 20;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

/**
 * The unified category list-row tile: a circular icon tile with a progress ring
 * around it. The ring carries the row's "fill" — burn% of the monthly limit
 * (status-colored) on the Categorias page, share-of-total (category accent) in the
 * transações category drawer. `percent: null` renders the track only; `showTrack:
 * false` drops the ring entirely while keeping the 44px footprint (income rows).
 */
export function CategoryRingTile({
  icon,
  visual,
  percent,
  ringClassName = '',
  ringColor,
  showTrack = true,
  className = '',
}: {
  icon: string | null;
  visual: CategoryTileVisual;
  percent: number | null;
  /** Ring color as a text-* class (stroke uses currentColor)… */
  ringClassName?: string;
  /** …or a literal color (category accent in the transações context). */
  ringColor?: string;
  showTrack?: boolean;
  className?: string;
}) {
  const clamped = percent == null ? 0 : Math.min(100, Math.max(0, percent));
  return (
    <span className={`relative grid size-11 shrink-0 place-items-center ${className}`}>
      <svg viewBox="0 0 44 44" className="absolute inset-0 size-full -rotate-90" aria-hidden>
        {showTrack && (
          <circle
            cx="22"
            cy="22"
            r={RING_RADIUS}
            fill="none"
            strokeWidth="2.5"
            className="stroke-current text-divider"
          />
        )}
        {showTrack && percent != null && (
          <circle
            cx="22"
            cy="22"
            r={RING_RADIUS}
            fill="none"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeDasharray={`${(RING_CIRCUMFERENCE * clamped) / 100} ${RING_CIRCUMFERENCE}`}
            className={`stroke-current ${ringClassName}`}
            style={ringColor ? { color: ringColor } : undefined}
          />
        )}
      </svg>
      <span
        className="grid size-8.5 place-items-center rounded-full"
        style={{ backgroundColor: visual.backgroundColor, color: visual.color }}
      >
        <CategoryIcon name={icon} className="size-4" />
      </span>
    </span>
  );
}
